import { from, of, timer, EMPTY } from "rxjs";
import { map, switchMap, distinctUntilChanged, catchError } from "rxjs/operators";
import { jwtDecode } from "jwt-decode";

import { authApi } from "../apis/authApi";
import fullstoryService from "../../services/fullstoryService";

const token$ = (store) => from(store).pipe(
    map((state) => state.auth.Token),
    distinctUntilChanged()
);

const authState$ = (store) => from(store).pipe(
    map((state) => ({
        isAuthenticated: state.auth.isAuthenticated,
        userData: state.auth.UserData,
        role: state.auth.Role,
        trialFlag: state.auth.TrialFlag,
    })),
    distinctUntilChanged((prev, curr) =>
        prev.isAuthenticated === curr.isAuthenticated &&
        prev.userData?.id === curr.userData?.id &&
        prev.role === curr.role
    )
);

const expiration$ = (token) => {
    try {
        const decoded = jwtDecode(token);
        const expiration = decoded.exp * 1000;
        return of(expiration);
    } catch (error) {
        return of(null);
    }
};

/**
 * Monitor authentication state changes for Fullstory user identification
 */
export const monitorAuthState = (store) => {
    authState$(store).pipe(
        switchMap((authState) => {
            if (authState.isAuthenticated && authState.userData) {
                // User is authenticated - identify in Fullstory
                const userData = {
                    uid: authState.userData.id || authState.userData.email,
                    email: authState.userData.email,
                    displayName: authState.userData.first_name && authState.userData.last_name
                        ? `${authState.userData.first_name} ${authState.userData.last_name}`
                        : authState.userData.username || authState.userData.email,
                    role: authState.role,
                    trialUser: authState.trialFlag,
                    accountStatus: authState.userData.is_active ? 'active' : 'inactive',
                    registrationDate: authState.userData.date_joined,
                    lastLogin: authState.userData.last_login,
                };

                fullstoryService.identifyUser(userData);

                // Track login event
                fullstoryService.trackLogin({
                    method: 'app_authentication',
                    role: authState.role,
                });

                return of(authState);
            } else {
                // User is not authenticated - anonymize in Fullstory
                fullstoryService.anonymizeUser();

                // Track logout event
                fullstoryService.trackLogout();

                return of(authState);
            }
        }),
        catchError((error) => {
            console.error('Auth monitoring error:', error);
            return EMPTY;
        })
    ).subscribe();
};

export const monitorTokenExpiration = (store) => {
    token$(store).pipe(
        switchMap((Token) => {
            if (!Token) {
                return EMPTY;
            }

            return expiration$(Token).pipe(
                switchMap((expiration) => {
                    if (!expiration || expiration <= Date.now()) {
                        store.dispatch(authApi.endpoints.refreshToken.initiate());
                        return EMPTY;
                    }

                    const expiresIn = expiration - Date.now();
                    const refreshTime = Math.max(0, expiresIn - 300000);

                    if (refreshTime <= 0) {
                        store.dispatch(authApi.endpoints.refreshToken.initiate());
                        return EMPTY;
                    }

                    return timer(refreshTime).pipe(
                        switchMap(() => {
                            store.dispatch(authApi.endpoints.refreshToken.initiate());
                            return EMPTY;
                        })
                    );
                }),
                catchError(() => EMPTY)
            );
        })
    ).subscribe();
};